import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { FileSearch, Network, ShieldCheck } from 'lucide-react';
import { SiteLayout } from '@/components/SiteLayout';
import { TrustSearch } from '@/components/TrustSearch';
import { WotReportSection } from '@/components/WotReportSection';

const DEFAULT_NPUB =
  'npub1marc26z8nh3xkj5rcx7ufkatvx6ueqhp5vfw9v5teq26z254renshtf3g0';

const decoded = nip19.decode(DEFAULT_NPUB);
const DEFAULT_PUBKEY = decoded.type === 'npub' ? decoded.data : '';

const FEATURES = [
  { icon: Network, label: 'Social graph analysis' },
  { icon: ShieldCheck, label: 'NIP-85 trusted assertions' },
  { icon: FileSearch, label: 'Shareable reports' },
] as const;

const Index = () => {
  const navigate = useNavigate();

  useSeoMeta({
    title: 'TrustWeb — Nostr Web of Trust Score',
    description:
      'How trusted is a Nostr identity? TrustWeb analyzes followers, mutuals, influence, engagement, and verification to compute a web of trust score for any npub.',
  });

  return (
    <SiteLayout>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="container pt-16 pb-12 sm:pt-24 sm:pb-16">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-medium text-glow">
            <ShieldCheck className="size-3.5" />
            Nostr web-of-trust oracle
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.1]">
            How deep does the{' '}
            <span className="bg-gradient-to-r from-primary to-glow bg-clip-text text-transparent">
              web of trust
            </span>{' '}
            vouch for you?
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            TrustWeb maps an identity's position in the Nostr social graph —
            followers, mutual trust, influence, engagement, and verification —
            and distills it into a single score.
          </p>

          <TrustSearch
            className="mt-8 max-w-2xl"
            defaultValue={DEFAULT_NPUB}
            onAnalyze={(_pubkey, npub) => navigate(`/${npub}`)}
          />

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-1.5">
                <Icon className="size-3.5 text-glow" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Live report for the default identity ─────────────────────── */}
      <section className="container pb-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-primary/25" />
          <span className="font-mono text-xs text-muted-foreground">
            live report ·{' '}
            <span className="text-glow">npub1marc26z…renshtf3g0</span>
          </span>
          <span className="h-px flex-1 bg-primary/25" />
        </div>
        <WotReportSection pubkey={DEFAULT_PUBKEY} />
      </section>
    </SiteLayout>
  );
};

export default Index;
